import React, { useCallback, useState } from "react";

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  useMediaQuery,
} from "@mui/material";

import { Modal } from "@covertree/ui";
import { formatCurrency } from "@covertree/utils";
import { Installment } from "@covertree/new-business-graphql";

interface Props {
  installments?: Installment[];
}

const desktopColumnNames = [
  "Due date",
  "Premium",
  "Fees",
  "Taxes",
  "Total amount due",
];

const mobileColumnNames = ["Due date", "Total amount due"];

export function PaymentSchedule({ installments }: Props) {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  if (!installments) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleModalOpen}
        sx={(theme) => ({
          marginTop: 2,
          height: {
            xs: theme.spacing(5),
            sm: theme.spacing(8),
          },
        })}
        fullWidth>
        View payment schedule
      </Button>
      <Modal
        title="Future Invoices"
        open={isModalOpen}
        maxWidth="md"
        onClose={handleModalClose}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {(isDesktop ? desktopColumnNames : mobileColumnNames).map(
                (columnName) => (
                  <TableCell key={columnName} sx={{ fontWeight: 500 }}>
                    {columnName}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {installments.map((data) => (
              <TableRow key={data.dueDate}>
                <TableCell>{data.dueDate}</TableCell>
                {isDesktop && (
                  <>
                    <TableCell>{formatCurrency(data.premium, 2)}</TableCell>
                    <TableCell>{formatCurrency(data.fees, 2)}</TableCell>
                    <TableCell>{formatCurrency(data.taxes, 2)}</TableCell>
                  </>
                )}
                <TableCell sx={{ "&": { color: "secondary.main" } }}>
                  {formatCurrency(data.premium + data.fees + data.taxes, 2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Modal>
    </>
  );
}
